/**
 * Print of the download button of a report: the report as it is on the screen, on one page of the width of A4,
 * with vector text. The "Print A4" button (hidden, see below), Ctrl+P and the batch print call `window.print()` directly
 * and stay on the A4 print styles; the button, like the batch print, expands the tables first, Ctrl+P prints them as
 * they are.
 * While printing, the report is laid out for a fixed width (the PDF does not depend on the screen of the manager),
 * the `@media print` rules are switched off and the page size is set to the size of the report; after printing
 * all of it is back. A browser where this was not checked, and a report over the page limit of a PDF, print with
 * the A4 print styles.
 */

// `false` brings the A4 print styles back for the download button.
const SCREEN_PRINT_ENABLED = true;
// The "Print A4" button is hidden until the decision about it; everything behind it stays working, `true` brings it back.
const PRINT_A4_BUTTON_ENABLED = false;

const PRINT_WIDTH = 1920;
const A4_WIDTH = 794;
// 14400 pt, the largest page of Acrobat
const MAX_PAGE_HEIGHT = 19200;
// nivo draws a chart for the measured width of its container, after the layout changed
const CHARTS_SETTLE = 400;
const CHARTS_TIMEOUT = 3000;

// a second click waits for the print in progress, so it cannot collapse the report before that print
let printInProgress: Promise<void> | undefined;

// Chromium on a desktop only: `userAgentData` does not exist in Firefox and Safari, and `(pointer: fine)` is false
// over a remote desktop.
const canPrintAsScreen = (): boolean =>
  SCREEN_PRINT_ENABLED &&
  (navigator as Navigator & { userAgentData?: { mobile: boolean } }).userAgentData?.mobile === false;

// where the download button already prints on A4, this button would repeat it
const isPrintA4Shown = (): boolean => PRINT_A4_BUTTON_ENABLED && canPrintAsScreen();

const switchOffPrintRules = (): (() => void) => {
  const changed: Array<readonly [CSSMediaRule, string]> = [];

  const walk = (rules: CSSRuleList) =>
    Array.from(rules).forEach((rule) => {
      if (rule instanceof CSSMediaRule && /\bprint\b/i.test(rule.media.mediaText)) {
        changed.push([rule, rule.media.mediaText]);
        rule.media.mediaText = 'not all';
      } else if (rule instanceof CSSGroupingRule) {
        walk(rule.cssRules);
      }
    });

  Array.from(document.styleSheets).forEach((sheet) => {
    try {
      walk(sheet.cssRules);
    } catch (error) {
      // a stylesheet of another origin cannot be read
      console.error(error);
    }
  });

  return () => changed.forEach(([rule, mediaText]) => (rule.media.mediaText = mediaText));
};

const chartWidths = (): string =>
  Array.from(document.querySelectorAll('[data-print-fit] svg'))
    .map((svg) => svg.getAttribute('width'))
    .join();

const waitForCharts = (): Promise<void> =>
  new Promise((resolve) => {
    const started = Date.now();
    let widths = chartWidths();
    let changed = started;

    const check = () => {
      const now = Date.now();
      const current = chartWidths();

      if (current !== widths) {
        widths = current;
        changed = now;
      }

      if (now - changed >= CHARTS_SETTLE || now - started >= CHARTS_TIMEOUT) resolve();
      else setTimeout(check, 50);
    };

    check();
  });

/**
 * The scale changes the height of the layout a little, so the height is measured under the scale: a calculated one
 * moves the end of the report to a second page, and cutting the overflow would lose it.
 */
const measureScaledHeight = (page: HTMLElement, zoom: number): number => {
  const root = document.documentElement;

  root.style.zoom = String(zoom);
  const height = Math.ceil(page.getBoundingClientRect().height);
  root.style.zoom = '';

  return height;
};

const print = async (page: HTMLElement): Promise<void> => {
  page.style.width = `${PRINT_WIDTH}px`;
  await waitForCharts();

  const restoreRules = switchOffPrintRules();
  const style = document.createElement('style');
  style.textContent = '[data-screen-print-hide] { display: none !important; } main { margin-top: 0 !important; }';
  document.head.appendChild(style);

  const restore = () => {
    restoreRules();
    style.remove();
    page.style.width = '';
  };

  const zoom = A4_WIDTH / PRINT_WIDTH;
  const height = measureScaledHeight(page, zoom);

  if (height > MAX_PAGE_HEIGHT) {
    restore();
    await waitForCharts();
    window.print();
    return;
  }

  style.textContent += `
    @page { size: ${A4_WIDTH}px ${height + 2}px; margin: 0; }
    @media print {
      html { zoom: ${zoom}; }
      html, body { background: #28282e !important; }
      #reportsPage { width: ${PRINT_WIDTH}px !important; overflow: visible !important; }
      #reportsPage, #reportsPage * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      body > *:not(#root) { display: none !important; }
    }`;

  window.addEventListener('afterprint', restore, { once: true });
  window.print();
};

const printAsScreen = (): Promise<void> => {
  const page = document.querySelector<HTMLElement>('#reportsPage');

  if (!page || !canPrintAsScreen()) {
    window.print();
    return Promise.resolve();
  }

  printInProgress ??= print(page).finally(() => {
    printInProgress = undefined;
  });

  return printInProgress;
};

export { isPrintA4Shown, printAsScreen };
